/**
 * Instant Battles — pure builders for the two themed quick fights described in
 * docs/reference/site/manual/instant-battles.md, battle_of_jutland.md and
 * battle_atlantic.md. Each drops the player straight into a pre-deployed fight
 * (no buy phase, no map conquest planning):
 *
 *  - **Jutland** — dreadnoughts vs cruisers. Red fields battleships + subs + a
 *    few destroyers; Yellow fields many cruisers + more destroyers (numerous,
 *    but out-gunned toe-to-toe).
 *  - **Wolfpack** — a convoy ambush. Red has transports + assorted escorts near
 *    a clump of un-owned cities; Yellow has a pack of submarines + a raider.
 *
 * "Layouts vary every battle by AI level and side": the *enemy* (AI-controlled)
 * side gets extra signature hulls and a larger required win margin as the
 * difficulty level rises, so a higher level is a harder fight. The player is the
 * favoured side (must out-score the foe by the ratio). Instant battles "cannot
 * be projected — every turn must be fought" (the UI offers no early-project).
 *
 * Pure: no UI/DOM/RNG/wall-clock — only addShip + a hand-built GameState.
 */
import { addShip } from './ships';
import { markCityTiles, separateShips } from './deploy';
import { newGameState, type City, type GameState, type ShipType, type Side } from './state';

export type InstantBattle = 'jutland' | 'wolfpack';

export interface InstantBattleDef {
  id: InstantBattle;
  name: string;
  blurb: string;
  /** what each side fields, shown in the chooser */
  redTheme: string;
  yellowTheme: string;
}

export const INSTANT_BATTLES: readonly InstantBattleDef[] = [
  {
    id: 'jutland',
    name: 'Battle of Jutland',
    blurb: 'The largest clash of dreadnoughts in naval history. Battleships dominate toe-to-toe; cruisers swarm and flank.',
    redTheme: 'Battleships + subs',
    yellowTheme: 'Cruisers + destroyers',
  },
  {
    id: 'wolfpack',
    name: 'Wolfpack — North Atlantic',
    blurb: 'A submarine pack ambushes a convoy of transports racing for safe harbors. Escorts are too few to stop them all.',
    redTheme: 'Convoy + escorts',
    yellowTheme: 'Submarine wolfpack',
  },
];

export interface InstantOpts {
  battle: InstantBattle;
  /** the side the player controls (default Red) */
  human?: Side;
  /** AI difficulty 1–11 (scales the enemy fleet + the win margin) */
  level: number;
  seed?: number;
}

/** A blank open-sea state of the given size, the player favoured at `ratio`%. */
function blankSea(W: number, H: number, human: Side, ratio: number, seed: number): GameState {
  const g = newGameState({
    size: 'small', seed, maxTurn: 16, budgetR: 0, budgetY: 0,
    forceRatio: ratio, forceFavored: human,
  });
  g.W = W;
  g.H = H;
  g.tiles = Array.from({ length: H }, () => Array.from({ length: W }, () => 'water' as const));
  g.cities = [];
  g.ships = [];
  g.nextId = 1;
  return g;
}

/** Place `types` on distinct water tiles spiralling out from a rally point, so
 *  no more than one ship per tile (stacking limit can't bite on turn one). */
function deploy(g: GameState, side: Side, types: readonly ShipType[], c0: number, r0: number): void {
  const tiles: [number, number][] = [];
  for (let radius = 0; tiles.length < types.length && radius < 9; radius++) {
    for (let dr = -radius; dr <= radius; dr++) {
      for (let dc = -radius; dc <= radius; dc++) {
        if (Math.max(Math.abs(dr), Math.abs(dc)) !== radius) continue;
        const c = c0 + dc;
        const r = r0 + dr;
        if (c < 0 || r < 0 || c >= g.W || r >= g.H) continue;
        if (g.tiles[r]![c] === 'land') continue;
        if (tiles.some(([tc, tr]) => tc === c && tr === r)) continue;
        tiles.push([c, r]);
      }
    }
  }
  types.forEach((t, i) => {
    const [c, r] = tiles[i] ?? [c0, r0];
    addShip(g, side, t, c, r);
  });
}

/** Extra signature hulls the AI side earns from its difficulty level. */
function aiBonus(level: number): number {
  return Math.floor((Math.max(1, Math.min(11, level)) - 1) / 2); // 0..5
}

/** Repeat a hull `n` times. */
function rep(t: ShipType, n: number): ShipType[] {
  return Array.from({ length: Math.max(0, n) }, () => t);
}

export function buildInstantBattle(opts: InstantOpts): GameState {
  const human: Side = opts.human ?? 'R';
  const ai: Side = human === 'R' ? 'Y' : 'R';
  const level = Math.max(1, Math.min(11, Math.round(opts.level)));
  const ratio = 20 + 2 * level; // harder levels demand a larger margin (manual)
  const bonus = aiBonus(level);
  const seed = opts.seed ?? (opts.battle === 'jutland' ? 0x101 : 0x202) + level + (human === 'R' ? 0 : 99);

  const extra = (side: Side, t: ShipType): ShipType[] => (side === ai ? rep(t, bonus) : []);

  if (opts.battle === 'jutland') {
    const g = blankSea(19, 11, human, ratio, seed);
    g.nCities = 5;
    const cities: City[] = [
      { c: 2, r: 5, name: 'Scapa Flow', vp: 200, owner: 'R', home: 'R' },
      { c: 16, r: 5, name: 'Wilhelmshaven', vp: 200, owner: 'Y', home: 'Y' },
      { c: 9, r: 1, name: 'Skagerrak', vp: 300, owner: null, home: null },
      { c: 9, r: 9, name: 'Horns Reef', vp: 300, owner: null, home: null },
      { c: 9, r: 5, name: 'Jutland Bank', vp: 400, owner: null, home: null },
    ];
    g.cities = cities;
    markCityTiles(g); // cities are impassable land — garrisons sit on adjacent water
    // garrison each home so it keeps scoring; then the maneuver fleets
    addShip(g, 'R', 'TR', 2, 5);
    addShip(g, 'Y', 'TR', 16, 5);
    deploy(g, 'R',
      ['BB', 'BB', 'BB', 'SS', 'SS', 'DD', 'DD', 'TR', ...extra('R', 'BB')], 4, 5);
    deploy(g, 'Y',
      ['CR', 'CR', 'CR', 'CR', 'DD', 'DD', 'DD', 'PT', 'TR', ...extra('Y', 'CR')], 14, 5);
    separateShips(g); // one ship per tile — the spiral can collide with garrisons
    g.phase = 'move';
    return g;
  }

  // wolfpack
  const g = blankSea(19, 11, human, ratio, seed);
  g.nCities = 6;
  const cities: City[] = [
    { c: 2, r: 5, name: 'Halifax', vp: 200, owner: 'R', home: 'R' },
    { c: 16, r: 8, name: 'U-Boat Pens', vp: 200, owner: 'Y', home: 'Y' },
    { c: 6, r: 2, name: "St. John's", vp: 300, owner: null, home: null },
    { c: 6, r: 8, name: 'Reykjavik', vp: 300, owner: null, home: null },
    { c: 9, r: 5, name: 'Mid-Ocean', vp: 400, owner: null, home: null },
    { c: 11, r: 2, name: 'The Faroes', vp: 200, owner: null, home: null },
  ];
  g.cities = cities;
  markCityTiles(g); // cities are impassable land — garrisons sit on adjacent water
  addShip(g, 'R', 'TR', 2, 5);
  addShip(g, 'Y', 'TR', 16, 8);
  // Red: a convoy of transports with assorted escorts (DD/PT/CR), near the clump
  deploy(g, 'R',
    ['TR', 'TR', 'AT', 'CR', 'DD', 'DD', 'PT', 'PT', ...extra('R', 'DD')], 4, 5);
  // Yellow: a wolfpack of submarines + a surface raider, lurking mid-ocean
  deploy(g, 'Y',
    ['SS', 'SS', 'SS', 'SS', 'DD', 'CR', ...extra('Y', 'SS')], 12, 5);
  separateShips(g); // one ship per tile — the spiral can collide with garrisons
  g.phase = 'move';
  return g;
}
